import { Box, Paper, Typography } from '@mui/material';
import CardDTO from '@src/adapters/dto/card/CardDTO';
import AttachmentItem from './AttachmentItem';
import { CardModificationType } from '@src/constants/CardModificationType';
import { AttachmentType } from '@src/adapters/dto/card/CardAttachmentDTO';

interface Params {
  card: CardDTO;
  isShowChanges: boolean;
}

export interface Attachment {
  id?: string;
  createdAt?: Date;
  type: AttachmentType;
  mimeType?: string;
  isActive: boolean;

  current: {
    uploadedUrl?: string;
    optimisedUrl?: string;
    thumbnailUrl?: string;
    isPublic: boolean;
    title: string;
  };
  isAdded?: boolean;
  isRemoved?: boolean;
}

export function AttachmentsTab({ card, isShowChanges }: Params) {
  const attachmentList: Attachment[] = (
    card.attachments?.filter((n) => n.isActive) ?? []
  ).map((att) => ({
    id: att.id,
    createdAt: att.createdAt ? new Date(att.createdAt) : undefined,
    type: att.type,
    mimeType: att.mimeType,
    isActive: att.isActive,
    current: {
      uploadedUrl: att.uploadedUrl,
      optimisedUrl: att.optimisedUrl,
      thumbnailUrl: att.thumbnailUrl,
      isPublic: att.isPublic,
      title: att.title ?? '',
    },
    isAdded: false,
  }));

  if (isShowChanges) {
    const addedMigrations = card.modifications.filter(
      (m) => m.type === CardModificationType.AttachmentAdded
    );

    for (const migration of addedMigrations) {
      const attachment = attachmentList.find(
        (c) => c.id === migration.entityId
      );

      if (attachment) {
        attachment.isAdded = true;
      }
    }

    const removedMigrations = card.modifications.filter(
      (m) => m.type === CardModificationType.AttachmentRemoved
    );

    for (const migration of removedMigrations) {
      if (migration.type === CardModificationType.AttachmentRemoved) {
        const wasAddedInSameMigration = addedMigrations.some(
          (m) => m.entityId === migration.entityId
        );

        if (wasAddedInSameMigration) {
          continue;
        }

        const removedAttachment = card.attachments?.find(
          (c) => c.id === migration.entityId
        );

        attachmentList.push({
          id: migration.entityId!,
          createdAt: migration.createdAt,
          type: AttachmentType.AttachmentRemoved,
          isActive: true,
          current: {
            uploadedUrl: removedAttachment?.uploadedUrl,
            optimisedUrl: removedAttachment?.optimisedUrl,
            thumbnailUrl: removedAttachment?.thumbnailUrl,
            isPublic: migration.old.isPublic,
            title: migration.old.title,
          },
          isRemoved: true,
        });
      }
    }
  }

  return (
    <Box>
      <Paper>
        <Typography
          variant="h6"
          sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          Attachments
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 3 }}>
          {(attachmentList ?? []).map((attachment) => {
            return (
              <AttachmentItem
                key={attachment.id}
                attachment={attachment}
                isAdded={attachment.isAdded ?? false}
                isRemoved={attachment.isRemoved ?? false}
              />
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
